// 3rd-party modules
import { Modal, Button, Input, Select, InputNumber, Checkbox, message, Space } from "antd";
import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";

// project modules
import CustomerSearchModal from "../customer/CustomerSearchModal";
import DatePickerComponent from "../shared/datePicker";
import Loader from "../shared/loader";
import useTranslation from "../../hooks/TranslationContext";
import { apiCall } from "../../helpers/apiHelper";
import { ApiResponse } from "../../models/response";
import { yupResolver } from "@hookform/resolvers/yup";

//apis
import * as CustomerApi from '../../apis/customerApi';
import * as DiscountCouponApi from '../../apis/discountCouponApi';

//models
import { CustomerModel } from "../../models/customer";

// defines
type Props = {
  open: boolean;
  closeOnSave?: boolean;
  onSave: () => void;
  onCancel: () => void;
  defaultValues?: any;
};

const { Option } = Select;

export default function DiscountCouponModal({ open, closeOnSave = false, defaultValues, onSave, onCancel }: Props) {
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation();
  const abortController = new AbortController();

  const schema = yup.object().shape({
    discountCouponId: yup.number().nullable(), // Only used for editing
    code: yup.string().required(t("code-is-required")),
    expireDate: yup.string().nullable(),
    type: yup.number().required(t("type-is-required")),
    amount: yup.number().nullable().min(1, t("amount-must-be-greater-than-0")),
    discountPercent: yup
      .number()
      .nullable()
      .min(1, t("discount-percent-must-be-greater-than-0"))
      .max(100, t("discount-percent-cannot-exceed-100")),
    discountPercentMaxAmount: yup.number().nullable(),
    customerId: yup.number().nullable(),
    customerName: yup.string().nullable(),
    isActive: yup.boolean().required(t("status-is-required")),
    discountPercentMinAmount: yup.number().nullable(),
    usageLimit: yup.number().required(t("usage-limit-is-required")),
  });

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValues || {
      isActive: true
    },
  });

  useEffect(() => {
    reset(defaultValues || {});

    if (defaultValues?.customerId) {
      getDataAsync(defaultValues.customerId);
    }
  }, [defaultValues, reset]);

  const getData = async (id: any, abortSignal?: AbortSignal) => {
    const response = await apiCall(CustomerApi.getById(id, abortSignal));

    return response.success ? CustomerModel.toClass((response.data?.value)) : new CustomerModel();
  };

  const getDataAsync = async (id: any) => {
    setLoading(true);
    const customer = await getData(id, abortController.signal);

    setValue("customerName", `${customer.name} - ${customer.mobile}`);
    setLoading(false);
  }

  const onSubmit = async (formData: any) => {
    let response: ApiResponse;

    setLoading(true);

    if (!formData?.discountCouponId)
      response = await apiCall(DiscountCouponApi.createItem(formData, abortController.signal));
    else
      response = await apiCall(DiscountCouponApi.updateItem(formData?.discountCouponId!, formData, abortController.signal));

    if (response.success) {
      message.success(t(`${!formData?.discountCouponId ? 'discount-coupon-added-successfully' : 'discount-coupon-edited-successfully'}`));

      reset();
      if (onSave) onSave();

      if (closeOnSave) {
        onCancel();
      }

    } else
      message.error(response.error?.value);

    setLoading(false);
  };

  const onCustomerSelect = (customer: CustomerModel) => {
    setValue("customerId", customer.customerId);
    setValue("customerName", `${customer.name} - ${customer.mobile}`);

    // Do something with the selected customer
    setIsCustomerModalOpen(false);
  };

  return (
    <Modal
      title={t(!defaultValues?.discountCouponId ? "create-discount-coupon" : "edit-discount-coupon")}
      open={open}
      onCancel={onCancel}
      footer={null}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Hidden Field for DiscountCouponID */}
        {!!defaultValues?.discountCouponId && (
          <Controller
            name="discountCouponId"
            control={control}
            render={({ field }) => <input type="hidden" {...field} />}
          />
        )}
        <div>
          <label className="block text-sm font-medium">{t("code")}</label>
          <Controller
            name="code"
            control={control}
            render={({ field }) => (
              <Input {...field} placeholder={t("enter-coupon-code")} />
            )}
          />
          {errors.code && errors.code.message && <p className="text-red-500 text-sm">{errors.code.message.toString()}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium">{t("expire-date")}</label>
          <Controller
            name="expireDate"
            control={control}
            render={({ field }) => (
              <DatePickerComponent
                placeholder={t("enter-expire-date-optional")}
                value={field.value}
                onChange={field.onChange} />
            )}
          />
          {errors.expireDate && errors.expireDate.message && <p className="text-red-500 text-sm">{errors.expireDate.message.toString()}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium">{t("type")}</label>
          <Controller
            name="type"
            control={control}
            render={({ field }) => (
              <Select className="w-full" {...field} placeholder={t("select-type")}>
                <Option value={0}>{t("fixed-amount")}</Option>
                <Option value={1}>{t("percentage")}</Option>
              </Select>
            )}
          />
          {errors.type && errors.type.message && <p className="text-red-500 text-sm">{errors.type.message.toString()}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium">{t("amount")}</label>
          <Controller
            name="amount"
            control={control}
            render={({ field }) => (
              <InputNumber
                {...field}
                className="w-full"
                placeholder={t("enter-amount-optional")}
              />
            )}
          />
          {errors.amount && errors.amount.message && <p className="text-red-500 text-sm">{errors.amount.message.toString()}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium">{t("discount-percent")}</label>
          <Controller
            name="discountPercent"
            control={control}
            render={({ field }) => (
              <InputNumber
                {...field}
                className="w-full"
                placeholder={t("enter-discount-percent-optional")}
              />
            )}
          />
          {errors.discountPercent && errors.discountPercent.message && (
            <p className="text-red-500 text-sm">{errors.discountPercent.message.toString()}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium">{t("discount-percent-max-amount")}</label>
          <Controller
            name="discountPercentMaxAmount"
            control={control}
            render={({ field }) => (
              <InputNumber
                {...field}
                className="w-full"
                placeholder={t("enter-max-discount-amount-optional")}
              />
            )}
          />
        </div>
        <div>
          <label className="block text-sm font-medium">{t("customer")}</label>
          <Controller
            name="customerName"
            control={control}
            render={({ field }) => (
              <Space.Compact style={{ width: '100%' }}>
                <Input
                  {...field}
                  className="w-full"
                  readOnly
                  placeholder={t("enter-customer-optional")} />
                <Button type="primary" onClick={() => setIsCustomerModalOpen(true)}>{t("search")}</Button>
              </Space.Compact>
            )}
          />
        </div>
        <div>
          {/* <label className="block text-sm font-medium">{t("is-active")}</label> */}
          <Controller
            name="isActive"
            control={control}
            render={({ field }) => (
              <Checkbox {...field} checked={field.value}>
                {t("active")}
              </Checkbox>
            )}
          />
          {errors.isActive && errors.isActive.message && (
            <p className="text-red-500 text-sm">{errors.isActive.message.toString()}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium">{t("discount-percent-min-amount")}</label>
          <Controller
            name="discountPercentMinAmount"
            control={control}
            render={({ field }) => (
              <InputNumber
                {...field}
                className="w-full"
                placeholder={t("enter-min-discount-amount-optional")}
              />
            )}
          />
        </div>
        <div>
          <label className="block text-sm font-medium">{t("usage-limit")}</label>
          <Controller
            name="usageLimit"
            control={control}
            render={({ field }) => (
              <InputNumber {...field} className="w-full" placeholder={t("enter-usage-limit")} />
            )}
          />
          {errors.usageLimit && errors.usageLimit.message && (
            <p className="text-red-500 text-sm">{errors.usageLimit.message.toString()}</p>
          )}
        </div>
        {!!loading && <Loader />}
        <div className="flex justify-end space-x-2 gap-2">
          <Button onClick={onCancel}>{t("cancel")}</Button>
          <Button type="primary" htmlType="submit">
            {t(!defaultValues?.discountCouponId ? "create" : "update")}
          </Button>
        </div>
      </form>
      <CustomerSearchModal
        open={isCustomerModalOpen}
        onSelect={onCustomerSelect}
        onCancel={() => setIsCustomerModalOpen(false)}
      />
    </Modal>
  );
};
